const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * The AI report's plain text as simple HTML that pastes cleanly into an email: numbered section
 * lines ("1. Project Summary") and lines ending in a colon ("HVAC:") become bold headings, runs
 * of "- " lines become a bulleted list, and everything else is a paragraph.
 */
export const reportTextToHtml = (text: string) => {
  const blocks: string[] = [];
  let bullets: string[] = [];
  const closeList = () => {
    if (bullets.length === 0) return;
    blocks.push(`<ul>${bullets.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`);
    bullets = [];
  };

  text.split(/\r?\n/).forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) {
      closeList();
      return;
    }
    const bullet = line.match(/^[-•*]\s+(.*)$/);
    if (bullet) {
      bullets.push(bullet[1]);
      return;
    }
    closeList();
    const isHeading = /^\d+\.\s+\S/.test(line) || (line.endsWith(':') && line.length <= 60);
    blocks.push(
      isHeading ? `<p><strong>${escapeHtml(line)}</strong></p>` : `<p>${escapeHtml(line)}</p>`,
    );
  });
  closeList();
  return blocks.join('');
};

/**
 * Copies the report formatted (HTML) where the browser allows it, with the plain text alongside
 * for apps that don't take HTML. Falls back to plain text alone.
 */
export const copyReportToClipboard = async (text: string) => {
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([reportTextToHtml(text)], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ]);
      return;
    } catch {
      // Some browsers refuse HTML; plain text below still works.
    }
  }
  await navigator.clipboard.writeText(text);
};
